import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";

export type ClientRobotAssignment = {
  robotClientId: string;
  motif: string;
  remplacementRequis: boolean;
  robotRemplacementId: string | null;
  robotRemplacementSn: string | null;
};

export function useAvailableReplacementRobots() {
  return useQuery<any[]>({
    queryKey: ["/api/robots", "replacements-available"],
    queryFn: async () => {
      const res = await authFetch("/api/robots/replacements-available");
      if (!res.ok) throw new Error("Impossible de charger les robots de remplacement");
      return res.json();
    },
    staleTime: 30_000,
  });
}

export function useOpenRobotLoans() {
  return useQuery<any[]>({
    queryKey: ["/api/robot-locations", "open", "pret"],
    queryFn: async () => {
      const res = await authFetch("/api/robot-locations?locationType=pret&open=true");
      if (!res.ok) throw new Error("Impossible de charger les prêts ouverts");
      return res.json();
    },
    staleTime: 30_000,
  });
}

export function useActiveRobotLoan(robotId: string | null, enabled = true) {
  return useQuery<any | null>({
    queryKey: ["/api/robots", robotId, "active-loan"],
    queryFn: async () => {
      const res = await authFetch(`/api/robots/${robotId}/loan/current`);
      if (!res.ok) throw new Error("Impossible de charger le prêt actif");
      return res.json();
    },
    enabled: !!robotId && enabled,
    staleTime: 30_000,
  });
}

export function useCloseRobotLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ robotId, locationId }: { robotId: string; locationId?: string }) => {
      const endpoint = locationId
        ? `/api/robot-locations/${locationId}/close`
        : `/api/robots/${robotId}/loan/close`;
      const res = await authFetch(endpoint, {
        method: "POST",
        body: JSON.stringify({ dateFin: new Date().toISOString() }),
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.message || "Impossible de clôturer le prêt");
      }
      return res.json();
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["/api/robots", variables.robotId, "active-loan"] });
      qc.invalidateQueries({ queryKey: ["/api/robot-locations", "open", "pret"] });
      qc.invalidateQueries({ queryKey: ["/api/robots"] });
    },
  });
}